import { LEAD_TYPE_LABELS } from "@/lib/lead-type";
import { STAGE_LABELS } from "@/lib/stages";
import type { Company } from "./types";

export type CompanySortField =
  | "name"
  | "status"
  | "lead_type"
  | "industry"
  | "address_city"
  | "created_at";
export type CompanySortDirection = "asc" | "desc";

export interface CompanySortState {
  field: CompanySortField;
  direction: CompanySortDirection;
}

export const DEFAULT_COMPANY_SORT: CompanySortState = {
  field: "created_at",
  direction: "desc",
};

export function isDefaultCompanySort(sort: CompanySortState): boolean {
  return (
    sort.field === DEFAULT_COMPANY_SORT.field &&
    sort.direction === DEFAULT_COMPANY_SORT.direction
  );
}

export function shouldFetchAllCompaniesForLocalSort(
  hasLocalListFilters: boolean,
  sort: CompanySortState,
): boolean {
  return hasLocalListFilters && !isDefaultCompanySort(sort);
}

type SortableCompany = Pick<
  Company,
  "name" | "status" | "lead_type" | "industry" | "address_city" | "created_at"
>;

function textValue(value: string | null | undefined): string | null {
  const normalized = value?.trim().toLowerCase() ?? "";
  return normalized || null;
}

function dateValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

function compareNullableText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: CompanySortDirection,
): number {
  const aText = textValue(a);
  const bText = textValue(b);
  if (!aText && !bText) return 0;
  if (!aText) return 1;
  if (!bText) return -1;
  const comparison = aText.localeCompare(bText);
  return direction === "asc" ? comparison : -comparison;
}

function compareNullableDate(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: CompanySortDirection,
): number {
  const aTime = dateValue(a);
  const bTime = dateValue(b);
  if (aTime === null && bTime === null) return 0;
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  const comparison = aTime - bTime;
  return direction === "asc" ? comparison : -comparison;
}

function compareCompanyField(
  a: SortableCompany,
  b: SortableCompany,
  sort: CompanySortState,
): number {
  switch (sort.field) {
    case "name":
      return compareNullableText(a.name, b.name, sort.direction);
    case "status":
      return compareNullableText(STAGE_LABELS[a.status], STAGE_LABELS[b.status], sort.direction);
    case "lead_type":
      return compareNullableText(
        LEAD_TYPE_LABELS[a.lead_type],
        LEAD_TYPE_LABELS[b.lead_type],
        sort.direction,
      );
    case "industry":
      return compareNullableText(a.industry, b.industry, sort.direction);
    case "address_city":
      return compareNullableText(a.address_city, b.address_city, sort.direction);
    case "created_at":
      return compareNullableDate(a.created_at, b.created_at, sort.direction);
  }
}

export function sortCompanies<T extends SortableCompany>(
  companies: readonly T[],
  sort: CompanySortState = DEFAULT_COMPANY_SORT,
): T[] {
  return companies
    .map((company, index) => ({ company, index }))
    .sort((a, b) => {
      const primary = compareCompanyField(a.company, b.company, sort);
      if (primary !== 0) return primary;

      const nameTieBreak = compareNullableText(a.company.name, b.company.name, "asc");
      if (nameTieBreak !== 0) return nameTieBreak;

      return a.index - b.index;
    })
    .map(({ company }) => company);
}

export function nextCompanySortState(
  current: CompanySortState,
  field: CompanySortField,
): CompanySortState {
  if (current.field === field) {
    return {
      field,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }

  return {
    field,
    direction: field === "created_at" ? "desc" : "asc",
  };
}
